import React, { useRef, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Eraser } from 'lucide-react';

/**
 * Reusable HTML5 canvas signature pad.
 * Emits base64 PNG data URL via onChange whenever the drawing changes.
 * Pass `value` to preload an existing signature (data URL).
 */
export const SignaturePad = ({ value, onChange, height = 160, testIdPrefix = 'signature' }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(!value);

  // Initialise + load preloaded value
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, height);

    if (value) {
      const img = new window.Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, height);
        setIsEmpty(false);
      };
      img.src = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = getPoint(e);
  };

  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    setIsEmpty(false);
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (onChange) onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, height);
    setIsEmpty(true);
    if (onChange) onChange('');
  };

  return (
    <div>
      <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          data-testid={`${testIdPrefix}-canvas`}
          style={{ width: '100%', height: `${height}px`, touchAction: 'none', cursor: 'crosshair' }}
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={end}
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-slate-500">
          {isEmpty ? 'Sign inside the box above using mouse or touch.' : 'Signature captured.'}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={clear}
          data-testid={`${testIdPrefix}-clear-btn`}
        >
          <Eraser size={14} className="mr-1" />
          Clear
        </Button>
      </div>
    </div>
  );
};

export default SignaturePad;
